import { useEffect, useState } from "react";
import { Asset, GetAssetResponseSchema } from "@forgepoint/shared";
import { Loader2, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AssetFileViewer } from "@/modules/technicalDesigns/AssetFileViewer";
import { fetchApi } from "@/lib/api";

interface AssetPreviewDialogProps {
  assetId: string | null;
  fileName?: string;
  onClose: () => void;
}

/**
 * Dialog that fetches and previews an asset using AssetFileViewer.
 * Shows loading/error states and includes a download button.
 * @param assetId - The ID of the asset to preview, or null to close
 * @param fileName - Optional file name to show in the dialog title
 * @param onClose - Callback when the dialog is closed
 */
export const AssetPreviewDialog = ({
  assetId,
  fileName,
  onClose,
}: AssetPreviewDialogProps) => {
  const [fullAsset, setFullAsset] = useState<Asset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!assetId) {
      setFullAsset(null);
      setError(null);
      return;
    }

    const fetchFullAsset = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchApi(`api/assets/${assetId}`, "GET");
        const validated = GetAssetResponseSchema.parse(response);
        if (!validated.ok) {
          throw new Error("Failed to fetch asset");
        }
        setFullAsset(validated.data.asset);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load asset");
        setFullAsset(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFullAsset();
  }, [assetId]);

  /**
   * Creates a temporary anchor element to trigger a file download.
   */
  const handleDownload = () => {
    if (!fullAsset) {
      return;
    }
    const link = document.createElement("a");
    link.href = fullAsset.externalUrl;
    link.download = fullAsset.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={!!assetId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="truncate mr-4">
              {fullAsset?.fileName ?? fileName}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden relative">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
          {!isLoading && !error && fullAsset && (
            <>
              <AssetFileViewer asset={fullAsset} className="h-full" />
              <Button
                onClick={handleDownload}
                size="sm"
                variant="secondary"
                className="absolute bottom-4 right-4 gap-2"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
